import * as React from 'react';
import { useSelector } from 'react-redux';
import ReactGridLayout from 'react-grid-layout';
import { ProgressBar } from 'react-bootstrap';
import { ChartDataPoints, ChartModel, ChartResponse } from 'Interfaces/ChartInterface';
import { saveChart } from 'actions/chartsActions';
import { JSONInterface } from 'Interfaces/JsonInterface';
import { StateInterface } from 'Interfaces/StateInterface';
import ChartBoxComponent from 'views/Charts/ChartBoxComponent';
import { fetchPOI } from 'actions/pois';
import { useAppDispatch } from 'index';
import { getLocalization } from 'global/global';
import { removeChartFromDashboard } from 'actions/dashboardActions';
import { ModalComponentNames, ModalInterface } from 'Interfaces/ModalInterface';
import { ConfirmationModalProps } from 'views/Modals/ConfirmationModal';
import { navigateAddModal, navigateRemoveModal } from 'actions/navigationAction';
import { processChartData } from 'views/Charts/utils/ChartData';
import { setChartDataPoints } from 'actions/chartsActions';
import { getRequestConfig } from '../../../api';
import * as ChartUtils from '../../Charts/utils';

interface Props {
  chart: ChartModel;
  dashboardId: number;
  canEdit: boolean;
  applyFilters: boolean;
  layout: ReactGridLayout.Layout[];
  onChartLoaded: () => void;
}

export default function DashboardChart(props: Props) {
  const dispatch = useAppDispatch();

  const abortController = React.useRef(new AbortController());
  const forms = useSelector((state: StateInterface) => state.forms);
  const locations = useSelector((state: StateInterface) => state.locations);
  const users = useSelector((state: StateInterface) => state.users);
  const filtersMenu = useSelector((state: StateInterface) => state.filtersMenu);
  const [chartResponse, setChartResponse] = React.useState<ChartResponse[]>([]);
  const [charts, setCharts] = React.useState<JSONInterface[]>([]);

  const [model, setModel] = React.useState(props.chart);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (chartResponse.length > 0) {
      const charts = ChartUtils.createCharts(
        props.chart, chartResponse, undefined, forms.collection, locations.collection, {}
      );
      setCharts(charts.map(c => {
        if (props.chart.type === 'RADAR') {
          const ch = {...c};
          ch.chart.height = 540;
          return ch;
        }
        return c;
      }));
      // props.onChartLoaded();
    } else {
      setCharts([]);
    }
  }, [chartResponse]);

  React.useEffect(() => {
    setCharts(charts.map(c => { return {...c}; }));
  }, [props.layout]);

  React.useEffect(() => {
    if (!loading) {
      const headers = getRequestConfig();
      if (model.type === 'RADAR') {
        const formIds = Object.keys(model.forms);
        const query = {
          id: model.dataPoints.join(',')
        };
        setLoading(true);
        const response = dispatch(fetchPOI(
          formIds[0], abortController.current.signal, undefined, undefined, query,
          ['Name', 'questionnaire_id'].concat(model.forms[formIds[0]].questions).join(',')
        ));
        response.then(res => res.json()).then(json => {
          setChartResponse(json);
          setLoading(false);
        }).catch(e => {
          console.log(e);
          setLoading(false);
        });
      } else {
        const tempModel = {...props.chart};
        if (props.applyFilters) {
          const locations: string[] = [];
          const levels: string[] = [];
          filtersMenu.selectedLocations.forEach(location => {
            locations.push(`${location.key}`);
            levels.push(`${Number(location.level) + 1}`);
          });
          tempModel.locations = locations.join(',');
          tempModel.locationLevels = levels.join(',');
          tempModel.fromDate = filtersMenu.selectedDates.from || '';
          tempModel.toDate = filtersMenu.selectedDates.to || '';
          tempModel.users = filtersMenu.selectedUsers.map(u => u.id).join(',');
        }
        setLoading(true);
        fetch(
          `/json/chart?chartModel=${encodeURIComponent(JSON.stringify(tempModel))}`, headers
        ).then(res => res.json()).then(json => {
          setLoading(false);
          setChartResponse(() => {
            void loadDataPoints(json);
            return json;
          });
        }).catch(() => {
          setLoading(false);
        });
      }
    }
  }, [props.chart, props.applyFilters, filtersMenu, model.dataPoints]);

  const switchChart = (id: string, action: boolean, name?: string) => {
    if (name?.startsWith('sort')) {
      const chart = chartResponse.find((cd) => cd.id === id);
      if (chart && chart.series.length > 0) {
        const newChart = ChartUtils.getBarChart(
          props.chart, chart, forms.collection, undefined, locations.collection, {}, action, name
        );
        const newCharts = charts.map(ch => {
          if (ch.responseId !== id) {
            return ch;
          }
          return newChart[0];
        });
        setCharts(newCharts);
      }
    } else if (name === 'cummulative') {
      const { chart: model } = props;
      if (model.combine === 'FIELD') {
        const charts = ChartUtils.getCombinedLineChart(
          model, chartResponse, forms.collection, action, locations.collection
        );
        setCharts(charts);
      } else if (model.combine === 'LOCATION') {
        const charts = ChartUtils.getCombinedLocationLineChart(
          model, chartResponse, forms.collection, action, locations.collection
        );
        setCharts(charts);
      } else {
        const chart = chartResponse.find((cd) => cd.id === id);
        if (chart) {
          const newChart = ChartUtils.getLineChart(model, chart, forms.collection, action);
          const newCharts = charts.map((ch) => {
            if (ch.responseId === id) {
              return newChart;
            }
            return ch;
          });
          setCharts(newCharts);
        }
      }
      return;
    }
  };

  const loadDataPoints = async (tempChartResponse) => {
    const { chart: model } = props;
    const chartDataPoints: ChartDataPoints = {};
    const { query, filters } = ChartUtils.getChartFilters(model);
    const keys = Object.keys(model.forms);
    for (const formId of keys) {
      const fields = [...model.forms[formId].questions];
      if (model.xaxis) {
        if (model.xaxis.indexOf('when-updated') > -1) {
          fields.push('modified');
        } else if (model.xaxis.indexOf('when-created') > -1) {
          fields.push('created');
        } else {
          fields.push(model.xaxis);
        }
      }
      if (model.grouping) {
        if (`1234`.indexOf(model.grouping) > -1) {
          fields.push(`location${model.grouping}`);
        } else {
          fields.push(model.grouping);
        }
      }
      const response = await dispatch(fetchPOI(
        formId, abortController.current.signal, undefined, filters, query,
        ['Name', 'questionnaire_id'].concat(fields).join(',')
      ));
      const json = await response.json();
      if (Array.isArray(json)) {
        // const { forms, locations, users } = this.props;
        const form = forms.collection.find(f => f.ref === formId);
        if (form) {
          chartDataPoints[formId] = processChartData(model, json, form, locations.collection, users);
        }
      }
    }
    const charts = ChartUtils.createCharts(
      model, tempChartResponse, undefined, forms.collection, locations.collection, chartDataPoints
    );
    dispatch(setChartDataPoints(`${model.id}`, {...chartDataPoints}));
    setCharts(charts);
  };

  const onRemoveChart = () => {
    const modalProps: ModalInterface<ConfirmationModalProps> = {
      component: ModalComponentNames.ConfirmationModal,
      props: {
        onClose: () => {
          dispatch(navigateRemoveModal(ModalComponentNames.ConfirmationModal));
        },
        onConfirm: () => {
          void dispatch(removeChartFromDashboard({
            dashboardId: props.dashboardId, chartId: Number(props.chart.id)
          }));
          dispatch(navigateRemoveModal(ModalComponentNames.ConfirmationModal));
        },
        localizations: {
          cancel: getLocalization('cancel'),
          confirm: getLocalization('remove'),
          confirmStyle: 'danger',
          header: (<label>{getLocalization('confirm')}</label>),
          body: (<p>{getLocalization('removeChart')}</p>)
        }
      }
    };
    dispatch(navigateAddModal(modalProps));
  };

  return (
    <>
      {loading && (
        <div className="dashboard-chart-container">
          <ProgressBar striped={true} variant={'primary'} animated={true} now={100} />
        </div>
      )}
      {!loading && chartResponse.length === 0 && charts.length === 0 && (
        <div className="dashboard-chart-container">
          <p>Error loading chart</p>
        </div>
      )}
      {charts.length > 0 ? charts.map((chart, index) => (
        <ChartBoxComponent
          key={`${props.chart.id}-chart-${chart['id']}-${index}-${chart['responseId']}`}
          id={chart['responseId']}
          index={index}
          chartType={props.chart.type}
          options={chart}
          switchChart={switchChart}
          addChartSvgs={() => null}
          model={model}
          updateModel={(m) => {
            setModel(m);
            void dispatch(saveChart(m));
          }}
          dashboard={true}
          removeChart={props.canEdit ? onRemoveChart : undefined}
        />
      )) : null}
    </>
  );
}
